import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ScenarioAnalysisTab, ScenarioAnalysisTabFlags } from "pages/app/scenario-analysis/components/ScenarioAnalysisTabBar";

/** Asks before leaving Detailed calculations mid-edit; `proceed` finishes the navigation once the farmer agrees. */
export type DetailedCalcsLeaveGuard = (proceed: (options?: { skipRecalculate?: boolean }) => void) => void;

export type DetailedCalcsView = "compare" | "detail";

export interface UseScenarioAnalysisTabsOptions {
  isDirty: boolean;
  isStale: boolean;
  isCalculating: boolean;
  hasCalculatedOnceRef: MutableRefObject<boolean>;
  recalculateInFlightRef: MutableRefObject<boolean>;
  recalculate: () => Promise<boolean>;
  leaveGuardRef?: MutableRefObject<DetailedCalcsLeaveGuard | null>;
}

/**
 * Every tab change goes through `goToTab`. Arrival on an output tab with outdated figures then
 * recalculates as an event, not as an effect that watches the tab and fires on stale values.
 */
export const useScenarioAnalysisTabs = ({
  isDirty,
  isStale,
  isCalculating,
  hasCalculatedOnceRef,
  recalculateInFlightRef,
  recalculate,
  leaveGuardRef,
}: UseScenarioAnalysisTabsOptions) => {
  const [activeTab, setActiveTab] = useState<ScenarioAnalysisTab>("worth-it");
  const [isAwaitingRecalc, setIsAwaitingRecalc] = useState(false);
  const [detailedCalcsScenarioId, setDetailedCalcsScenarioId] = useState<string | null>(null);
  const [detailedCalcsIsEditing, setDetailedCalcsIsEditing] = useState(false);
  const [detailedCalcsView, setDetailedCalcsView] = useState<DetailedCalcsView>("compare");
  const editOriginRef = useRef<ScenarioAnalysisTab | null>(null);

  // Null while the figures match the inputs. Otherwise the tabs seen since they stopped matching,
  // which is every tab that must show no change dot.
  const [seenSinceChange, setSeenSinceChange] = useState<ScenarioAnalysisTab[] | null>(null);
  const wasDirtyRef = useRef(false);
  const seenRef = useRef(seenSinceChange);
  seenRef.current = seenSinceChange;
  const isEditingRef = useRef(detailedCalcsIsEditing);
  isEditingRef.current = detailedCalcsIsEditing;
  // The dots as they stood when editing began, so a discard that changes nothing can put them back.
  const seenBeforeEditRef = useRef<ScenarioAnalysisTab[] | null>(null);

  useEffect(() => {
    // We re-mark the tabs only on a new change. The run that follows must not, because the farmer has not opened every tab.
    const isNewChange = isDirty && !wasDirtyRef.current && hasCalculatedOnceRef.current;
    wasDirtyRef.current = isDirty;

    setSeenSinceChange((seen) => {
      if (isNewChange) return [activeTab];
      if (seen === null) return null;
      return seen.includes(activeTab) ? seen : [...seen, activeTab];
    });
  }, [isDirty, activeTab, hasCalculatedOnceRef]);

  // Edits are not final until Done, so no tab is marked as changed while editing.
  const changeDots = useMemo<ScenarioAnalysisTabFlags>(
    () => ({
      "worth-it": !detailedCalcsIsEditing && seenSinceChange !== null && !seenSinceChange.includes("worth-it"),
      "detailed-calcs": !detailedCalcsIsEditing && seenSinceChange !== null && !seenSinceChange.includes("detailed-calcs"),
    }),
    [seenSinceChange, detailedCalcsIsEditing]
  );

  const updateEditing = useCallback((isEditing: boolean) => {
    if (isEditing && !isEditingRef.current) seenBeforeEditRef.current = seenRef.current;
    setDetailedCalcsIsEditing(isEditing);
  }, []);

  const restoreChangeDots = useCallback(() => setSeenSinceChange(seenBeforeEditRef.current), []);

  const navigate = useCallback(
    (tab: ScenarioAnalysisTab, afterPaint?: () => void, options: { skipRecalculate?: boolean } = {}) => {
      setActiveTab(tab);
      if (tab !== "detailed-calcs") {
        updateEditing(false);
        editOriginRef.current = null;
      }
      if (afterPaint) requestAnimationFrame(afterPaint);

      if (options.skipRecalculate) return;
      if (tab === "scenarios" || !hasCalculatedOnceRef.current) return;
      if (!isStale || isCalculating || recalculateInFlightRef.current) return;
      setIsAwaitingRecalc(true);
      recalculate()
        .catch(() => {})
        .finally(() => setIsAwaitingRecalc(false));
    },
    [isStale, isCalculating, recalculate, hasCalculatedOnceRef, recalculateInFlightRef, updateEditing]
  );

  const goToTab = useCallback(
    (tab: ScenarioAnalysisTab, afterPaint?: () => void, options: { skipRecalculate?: boolean } = {}) => {
      const guard = leaveGuardRef?.current;
      if (!detailedCalcsIsEditing || tab === "detailed-calcs" || !guard) {
        navigate(tab, afterPaint, options);
        return;
      }
      guard((proceedOptions) => navigate(tab, afterPaint, { skipRecalculate: options.skipRecalculate || proceedOptions?.skipRecalculate }));
    },
    [detailedCalcsIsEditing, leaveGuardRef, navigate]
  );

  // Must not switch scenario while the tab is editing another: that bypasses DetailedCalculationsTab's discard prompt.
  const openDetailedCalculations = useCallback(
    (scenarioId?: string, options: { startEditing?: boolean; returnTo?: ScenarioAnalysisTab } = {}) => {
      if (scenarioId) setDetailedCalcsScenarioId(scenarioId);
      // Opened for one scenario or to edit, the farmer wants that scenario's figures, not the overview.
      setDetailedCalcsView(scenarioId || options.startEditing ? "detail" : "compare");
      // We skip the run when editing starts. The edit makes the figures stale again, so the run is wasted.
      goToTab("detailed-calcs", undefined, { skipRecalculate: Boolean(options.startEditing) });
      updateEditing(Boolean(options.startEditing));
      editOriginRef.current = options.returnTo ?? null;
    },
    [goToTab, updateEditing]
  );

  // Done keeps the edits, so its trip back to the origin tab skips the leave guard.
  const setDetailedCalcsEditing = useCallback(
    (isEditing: boolean, afterReturn?: () => void) => {
      updateEditing(isEditing);
      if (isEditing) return;
      const origin = editOriginRef.current;
      editOriginRef.current = null;
      // With no origin, Done stays on this tab and runs the edited figures the same way arriving would.
      navigate(origin ?? "detailed-calcs", origin ? afterReturn : undefined);
    },
    [navigate, updateEditing]
  );

  return {
    activeTab,
    goToTab,
    changeDots,
    restoreChangeDots,
    isAwaitingRecalc,
    detailedCalcsScenarioId,
    setDetailedCalcsScenarioId,
    detailedCalcsView,
    setDetailedCalcsView,
    detailedCalcsIsEditing,
    setDetailedCalcsEditing,
    openDetailedCalculations,
  };
};
